/**
 * @format
 */
import { Inject, Injectable } from '@angular/core';
import {
    GenericPlanEducationOption,
    PlanStudyRightService,
    PlanValidationResult,
    PlanValidationTs,
    ValidatablePlan,
} from 'common-typescript';
import {
    Attainment,
    CourseUnit,
    CourseUnitSelection,
    CustomStudyDraft,
    Education,
    EntityWithRule,
    Grade,
    GradeScale,
    LocalId,
    Module,
    OtmId,
    PlanValidationState,
    StudyRight,
    SubstituteFor,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import {
    COLOR_SERVICE,
    COMMON_MODULE_SERVICE,
    PLAN_VALIDATION_RESULT_HELPER,
    VALID_ATTAINMENT_FILTER_SERVICE,
} from '../ajs-upgraded-modules';
import { CommonGradeAverageService } from './common-grade-average.service';

export interface PlanData {
    education: Education;
    modulesById: { [id: string]: Module };
    courseUnitsById: { [id: string]: CourseUnit };
    attainmentsById: { [id: string]: Attainment };
    customStudyDraftsById: { [id: string]: CustomStudyDraft };
}

/**
 * The fields of a plan validation result that plan state consumers read. The validator adds more, see RuleContext.getResults.
 * Every field is optional because a module that has an attainment but no validation result gets only state and credits.
 */
export interface PlanItemValidationResult {
    state?: `${PlanValidationState}`;
    plannedCredits?: { min: number; max: number | null };
    attainedCredits?: number;
    implicitCourseUnitIds?: OtmId[];
    invalidSelection?: boolean;
    invalidAccordingToModuleContentApproval?: boolean;
}

export interface ModuleValidationResult extends PlanItemValidationResult {
    isModuleContentApproved?: boolean;
    hasInvalidOrMissingSelections?: boolean;
}

export interface SelectionValidationState {
    invalidSelection?: boolean;
    hasModuleContentApproval?: boolean;
    invalidSelectionAccordingToModuleContentApproval?: boolean;
    isInvalidAccordingToModuleContentApprovalRequested?: boolean;
    isInvalidAccordingToModuleContentApprovalAccepted?: boolean;
}

export interface EducationStateObject {
    type: 'EducationStateObject';
    educationValidationResult: PlanItemValidationResult;
    selectedCourseUnitIds: OtmId[];
    selectedModuleIds: OtmId[];
    selectedCustomCourseUnitAttainmentIds: OtmId[];
    selectedCustomModuleAttainmentIds: OtmId[];
    selectedCustomStudyDraftIds: LocalId[];
    implicitCourseUnitIds: OtmId[];
    elementId: string;
}

export interface ModuleStateObject extends SelectionValidationState {
    type: 'ModuleStateObject';
    studyRightState: string;
    attainmentId: OtmId;
    grade?: Grade;
    attainmentExpiryDate: string;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    moduleValidationResult: ModuleValidationResult;
    colorCategoryCssClass: string;
    planState?: string;
    selectedCourseUnitIds: OtmId[];
    selectedModuleIds: OtmId[];
    selectedCustomCourseUnitAttainmentIds: OtmId[];
    selectedCustomModuleAttainmentIds: OtmId[];
    selectedCustomStudyDraftIds: LocalId[];
    implicitCourseUnitIds?: OtmId[];
    gradeAverage?: number;
    isInPlan: boolean;
    elementId: string;
}

export interface CourseUnitStateObject extends SelectionValidationState {
    type: 'CourseUnitStateObject';
    attainmentId: OtmId;
    grade?: Grade;
    attainmentExpiryDate: string;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    courseUnitValidationResult?: PlanItemValidationResult;
    colorCategoryCssClass: string;
    isSubstituted: boolean;
    substitutedBy: OtmId[];
    substituteFor: SubstituteFor[];
    isInPlan: boolean;
    isInPlanAsSubstitute: boolean;
    newerVersionAvailable?: boolean;
    selectedAssessmentItemIds: OtmId[];
    courseUnitSelection: CourseUnitSelection;
}

export interface CustomModuleAttainmentStateObject extends SelectionValidationState {
    type: 'CustomModuleAttainmentStateObject';
    grade: Grade;
    attainmentExpiryDate: string;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    customModuleAttainmentValidationResult?: ModuleValidationResult;
    colorCategoryCssClass: string;
    planState?: string;
    selectedCourseUnitIds: OtmId[];
    selectedModuleIds: OtmId[];
    selectedCustomCourseUnitAttainmentIds: OtmId[];
    selectedCustomModuleAttainmentIds: OtmId[];
    gradeAverage: number;
    isInPlan: boolean;
}

export interface CustomCourseUnitAttainmentStateObject extends SelectionValidationState {
    type: 'CustomCourseUnitAttainmentStateObject';
    grade: Grade;
    attainmentExpiryDate: string;
    isAttainmentAboutToExpire: boolean;
    selectedParentModuleId: OtmId;
    customCourseUnitAttainmentValidationResult?: PlanItemValidationResult;
    colorCategoryCssClass: string;
    isInPlan: boolean;
}

export interface PlanStateAndData {
    planData: PlanData;
    planStateObject: PlanStateObject;
}

export interface PlanStateObject {
    education: EducationStateObject;
    modules: { [id: string]: ModuleStateObject };
    courseUnits: { [id: string]: CourseUnitStateObject };
    customModuleAttainments: { [id: string]: CustomModuleAttainmentStateObject };
    customCourseUnitAttainments: { [id: string]: CustomCourseUnitAttainmentStateObject };
    studyRight: StudyRight;
}

@Injectable({ providedIn: 'root' })
export class PlanStateService {
    constructor(
        @Inject(VALID_ATTAINMENT_FILTER_SERVICE) private validAttainmentFilterService: any,
        @Inject(PLAN_VALIDATION_RESULT_HELPER) private planValidationResultHelper: any,
        @Inject(COLOR_SERVICE) private colorService: any,
        private commonGradeAverageService: CommonGradeAverageService,
        @Inject(COMMON_MODULE_SERVICE) private commonModuleService: any,
    ) {}
    versionChanged = false;

    getPlanStateAndData(
        education: Education,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        educationOptions: GenericPlanEducationOption[],
        gradeScalesById: { [id: string]: GradeScale },
        studyRight: StudyRight,
        outDatedCourseUnitsInPlan?: [CourseUnit, CourseUnit][],
    ): PlanStateAndData {
        const planStateObject: PlanStateObject = {
            education: this.getEducationStateObject(education, validatablePlan, planValidationResult),
            modules: this.getModuleStates(validatablePlan, educationOptions, planValidationResult, gradeScalesById),
            courseUnits: this.getCourseUnitStateObject(validatablePlan, planValidationResult, gradeScalesById, outDatedCourseUnitsInPlan),
            customModuleAttainments: this.getCustomModuleAttainmentStateObject(validatablePlan, planValidationResult, gradeScalesById),
            customCourseUnitAttainments: this.getCustomCourseUnitAttainmentStateObject(
                validatablePlan,
                planValidationResult,
                gradeScalesById,
            ),
            studyRight,
        };
        return {
            planData: this.getPlanData(education, validatablePlan),
            planStateObject,
        };
    }

    private getPlanData(education: Education, validatablePlan: ValidatablePlan): PlanData {
        const modules = _.values(validatablePlan.modulesById) as Module[];
        const courseUnits = _.values(validatablePlan.courseUnitsById);
        const attainments = [
            ...modules.map(module => validatablePlan.getModuleAttainment(module.id)),
            ...courseUnits.map(courseUnit => validatablePlan.getCourseUnitAttainment(courseUnit.id)),
            ..._.values(validatablePlan.customModuleAttainmentsById),
            ..._.values(validatablePlan.customCourseUnitAttainmentsById),
        ].filter(Boolean);
        return {
            education,
            modulesById: _.keyBy(modules, 'id'),
            courseUnitsById: _.keyBy(courseUnits, 'id'),
            attainmentsById: _.keyBy(attainments, 'id'),
            customStudyDraftsById: _.keyBy(validatablePlan.plan.customStudyDrafts || [], 'id'),
        };
    }

    private getEducationStateObject(
        education: Education,
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
    ): EducationStateObject {
        let childModules = validatablePlan.getSelectedModulesUnderModule(education);
        if (_.every(childModules, { type: 'DegreeProgramme' })) {
            childModules = this.commonModuleService.getDegreeProgrammesOrderedByTypeUrn(childModules);
        }
        return {
            type: 'EducationStateObject',
            educationValidationResult: planValidationResult.rootValidationResult,
            selectedCourseUnitIds: _.map(validatablePlan.getSelectedCourseUnitsUnderModule(education), 'id'),
            selectedModuleIds: _.map(childModules, 'id'),
            selectedCustomCourseUnitAttainmentIds: _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentIdsUnderModule(education)),
            selectedCustomModuleAttainmentIds: _.uniq(validatablePlan.getSelectedCustomModuleAttainmentIdsUnderModule(education)),
            selectedCustomStudyDraftIds: _.map(validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(education.id), 'id'),
            implicitCourseUnitIds: planValidationResult.rootValidationResult?.implicitCourseUnitIds || [],
            elementId: `edu:${education.id}`,
        };
    }

    private getModuleStates(
        validatablePlan: ValidatablePlan,
        educationOptions: GenericPlanEducationOption[],
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: ModuleStateObject } {
        const moduleStateObjectsById: { [id: string]: ModuleStateObject } = {};
        const modulesInPlan = _.values(validatablePlan.modulesById);
        _.forEach(modulesInPlan, module => {
            const moduleAttainment = validatablePlan.getModuleAttainment(module.id);
            const moduleSelection = validatablePlan.getModuleSelection(module.id);
            const selectedParent = validatablePlan.getParentModuleOrCustomModuleAttainmentForModule(module) as EntityWithRule;
            const validationResult = this.planValidationResultHelper.getModuleValidationResult(module.id, planValidationResult);
            moduleStateObjectsById[module.id] = {
                type: 'ModuleStateObject',
                studyRightState: this.getStudyRightStateForModule(module, educationOptions, validatablePlan),
                attainmentId: moduleAttainment ? moduleAttainment.id : null,
                ...(moduleAttainment
                    ? { grade: this.getGrade(moduleAttainment.gradeId, gradeScalesById[moduleAttainment.gradeScaleId]) }
                    : {}),
                attainmentExpiryDate: moduleAttainment ? moduleAttainment.expiryDate : null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(moduleAttainment, validatablePlan),
                selectedParentModuleId: _.get(moduleSelection, 'parentModuleId'),
                ...(validationResult
                    ? {
                          moduleValidationResult: validationResult,
                          planState: PlanValidationTs.getPlanStateForModule(module, validatablePlan, planValidationResult),
                          implicitCourseUnitIds: validationResult.implicitCourseUnitIds || [],
                          ...this.getSelectionValidationState(validationResult, selectedParent, planValidationResult),
                      }
                    : {
                          moduleValidationResult: moduleAttainment
                              ? {
                                    state: 'ATTAINED',
                                    plannedCredits: { min: moduleAttainment.credits, max: moduleAttainment.credits },
                                    attainedCredits: moduleAttainment.credits,
                                }
                              : {},
                      }),
                colorCategoryCssClass: `cu-color-${this.colorService.getModuleColorCategory(validatablePlan, module.id)}`,
                selectedCourseUnitIds: _.map(validatablePlan.getSelectedCourseUnitsUnderModule(module), 'id'),
                selectedModuleIds: _.map(validatablePlan.getSelectedModulesUnderModule(module), 'id'),
                selectedCustomCourseUnitAttainmentIds: _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentIdsUnderModule(module)),
                selectedCustomModuleAttainmentIds: _.uniq(validatablePlan.getSelectedCustomModuleAttainmentIdsUnderModule(module)),
                selectedCustomStudyDraftIds: _.map(validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(module.id), 'id'),
                ...(module.type !== 'GroupingModule'
                    ? { gradeAverage: this.getGradeAverage(module, moduleAttainment, validatablePlan, gradeScalesById['sis-0-5']) }
                    : {}),
                isInPlan: validatablePlan.isModuleInPlan(module.id),
                elementId: `mod:${module.id}`,
            };
        });
        return moduleStateObjectsById;
    }

    private getCourseUnitStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
        outDatedCourseUnitsInPlan?: [CourseUnit, CourseUnit][],
    ): { [id: string]: CourseUnitStateObject } {
        const courseUnitStatesById: { [id: string]: CourseUnitStateObject } = {};
        const courseUnitsInPlan = _.values(validatablePlan.courseUnitsById);
        _.forEach(courseUnitsInPlan, courseUnit => {
            const courseUnitAttainment = validatablePlan.getCourseUnitAttainment(courseUnit.id);
            const courseUnitSelection = validatablePlan.getCourseUnitSelection(courseUnit.id) as CourseUnitSelection;
            const selectedParentModuleId = _.get(courseUnitSelection, 'parentModuleId');
            const validationResult = this.planValidationResultHelper.getCourseUnitValidationResult(courseUnit.id, planValidationResult);
            const module = _.get(validatablePlan.modulesById, selectedParentModuleId) as EntityWithRule;
            courseUnitStatesById[courseUnit.id] = {
                type: 'CourseUnitStateObject',
                attainmentId: courseUnitAttainment ? courseUnitAttainment.id : null,
                ...(courseUnitAttainment
                    ? { grade: this.getGrade(courseUnitAttainment.gradeId, gradeScalesById[courseUnitAttainment.gradeScaleId]) }
                    : {}),
                attainmentExpiryDate: courseUnitAttainment ? courseUnitAttainment.expiryDate : null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(courseUnitAttainment, validatablePlan),
                selectedParentModuleId,
                ...(validationResult
                    ? {
                          courseUnitValidationResult: validationResult,
                          ...this.getSelectionValidationState(validationResult, module, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getCourseUnitColorCategory(validatablePlan, courseUnit.id)}`,
                isInPlan: validatablePlan.isCourseUnitInPlan(courseUnit),
                isInPlanAsSubstitute: validatablePlan.isCourseUnitInPlanAsSubstitute(courseUnit),
                isSubstituted: !_.isEmpty(_.get(courseUnitSelection, 'substitutedBy')),
                substitutedBy: _.get(courseUnitSelection, 'substitutedBy'),
                substituteFor: _.get(courseUnitSelection, 'substituteFor'),
                newerVersionAvailable: _.some(outDatedCourseUnitsInPlan, outDatedCourseUnit => outDatedCourseUnit[0].id === courseUnit.id),
                selectedAssessmentItemIds: _.map(validatablePlan.getSelectedAssessmentItems(courseUnit), ai => ai.id),
                courseUnitSelection,
            };
        });
        return courseUnitStatesById;
    }

    private getCustomModuleAttainmentStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: CustomModuleAttainmentStateObject } {
        const customModuleAttainmentStatesById: { [id: string]: CustomModuleAttainmentStateObject } = {};
        const customModuleAttainmentsPlan = _.values(validatablePlan.customModuleAttainmentsById);
        _.forEach(customModuleAttainmentsPlan, customModuleAttainment => {
            const selection = validatablePlan.customModuleAttainmentIdSelectionMap[customModuleAttainment.id];
            const selectedParent = validatablePlan.getParentModuleOrCustomModuleAttainmentForCustomAttainment(
                customModuleAttainment,
            ) as EntityWithRule;
            const validationResult = this.planValidationResultHelper.getCustomModuleAttainmentValidationResult(
                customModuleAttainment.id,
                planValidationResult,
            );
            customModuleAttainmentStatesById[customModuleAttainment.id] = {
                type: 'CustomModuleAttainmentStateObject',
                grade: this.getGrade(customModuleAttainment.gradeId, gradeScalesById[customModuleAttainment.gradeScaleId]),
                attainmentExpiryDate: customModuleAttainment.expiryDate,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(customModuleAttainment, validatablePlan),
                selectedParentModuleId: _.get(selection, 'parentModuleId'),
                ...(validationResult
                    ? {
                          customModuleAttainmentValidationResult: validationResult,
                          planState: PlanValidationTs.getPlanStateForModule(customModuleAttainment, validatablePlan, planValidationResult),
                          ...this.getSelectionValidationState(validationResult, selectedParent, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getModuleColorCategory(validatablePlan, customModuleAttainment.id)}`,
                selectedCourseUnitIds: _.map(
                    validatablePlan.getSelectedCourseUnitsUnderCustomModuleAttainment(customModuleAttainment),
                    'id',
                ),
                selectedModuleIds: _.map(validatablePlan.getSelectedModulesUnderCustomModuleAttainment(customModuleAttainment), 'id'),
                selectedCustomCourseUnitAttainmentIds: _.map(
                    _.uniq(validatablePlan.getSelectedCustomCourseUnitAttainmentsUnderCustomModuleAttainment(customModuleAttainment)),
                    'id',
                ),
                selectedCustomModuleAttainmentIds: _.map(
                    _.uniq(validatablePlan.getSelectedCustomModuleAttainmentsUnderCustomModuleAttainment(customModuleAttainment)),
                    'id',
                ),
                gradeAverage: this.getGradeAverage(null, customModuleAttainment, validatablePlan, gradeScalesById['sis-0-5']),
                isInPlan: validatablePlan.isModuleAttainmentInPlan(customModuleAttainment.id),
            };
        });
        return customModuleAttainmentStatesById;
    }

    private getCustomCourseUnitAttainmentStateObject(
        validatablePlan: ValidatablePlan,
        planValidationResult: PlanValidationResult,
        gradeScalesById: { [id: string]: GradeScale },
    ): { [id: string]: CustomCourseUnitAttainmentStateObject } {
        const customCourseUnitAttainmentStatesById: { [id: string]: CustomCourseUnitAttainmentStateObject } = {};
        const customCourseUnitAttainmentsInPlan = _.values(validatablePlan.customCourseUnitAttainmentsById);
        _.forEach(customCourseUnitAttainmentsInPlan, customCourseUnitAttainment => {
            const selection = validatablePlan.customCourseUnitAttainmentIdSelectionMap[customCourseUnitAttainment.id];
            const selectedParentModuleId = _.get(selection, 'parentModuleId');
            const validationResult = this.planValidationResultHelper.getCustomCourseUnitAttainmentValidationResult(
                customCourseUnitAttainment.id,
                planValidationResult,
            );
            const module = _.get(validatablePlan.modulesById, selectedParentModuleId) as EntityWithRule;
            customCourseUnitAttainmentStatesById[customCourseUnitAttainment.id] = {
                type: 'CustomCourseUnitAttainmentStateObject',
                grade: this.getGrade(customCourseUnitAttainment.gradeId, gradeScalesById[customCourseUnitAttainment.gradeScaleId]),
                attainmentExpiryDate: customCourseUnitAttainment.expiryDate || null,
                isAttainmentAboutToExpire: this.isAttainmentAboutToExpire(customCourseUnitAttainment, validatablePlan),
                selectedParentModuleId,
                ...(validationResult
                    ? {
                          customCourseUnitAttainmentValidationResult: validationResult,
                          ...this.getSelectionValidationState(validationResult, module, planValidationResult),
                      }
                    : {}),
                colorCategoryCssClass: `cu-color-${this.colorService.getCourseUnitColorCategory(validatablePlan, customCourseUnitAttainment.id)}`,
                isInPlan: validatablePlan.isCustomCourseUnitAttainmentInPlan(customCourseUnitAttainment),
            };
        });
        return customCourseUnitAttainmentStatesById;
    }

    private getStudyRightStateForModule(
        module: EntityWithRule,
        educationOptions: GenericPlanEducationOption[],
        validatablePlan: ValidatablePlan,
    ): string {
        if (!module || !educationOptions) {
            return null;
        }
        const matchingEducationOption = PlanStudyRightService.getMatchingEducationOption(module.groupId, educationOptions, validatablePlan);
        if (!!matchingEducationOption && matchingEducationOption.isInPlan && !matchingEducationOption.isInPlanAsMinor) {
            return matchingEducationOption.studyRightState;
        }
        return null;
    }

    private isAttainmentAboutToExpire(attainment: Attainment, validatablePlan: ValidatablePlan): boolean {
        if (
            attainment &&
            attainment.expiryDate &&
            this.validAttainmentFilterService.isAttainmentAboutToExpire(attainment) &&
            !this.validAttainmentFilterService.isAttached(attainment, validatablePlan.getAllAttainments())
        ) {
            return true;
        }
        return false;
    }

    private getGrade(gradeId: number, gradeScale: GradeScale): Grade {
        return _.find(gradeScale.grades, grade => grade.localId === gradeId);
    }

    private getGradeAverage(
        module: EntityWithRule,
        moduleAttainment: Attainment,
        validatablePlan: ValidatablePlan,
        gradeScale: GradeScale,
    ): number {
        let gradeAverageResult;
        let attainmentIds;
        const method = 'COURSE_UNIT_AND_EMPTY_MODULE_ARITHMETIC_MEAN_WEIGHTED_BY_CREDITS';
        const allAttainments = _.values(validatablePlan.attainmentsById);
        if (moduleAttainment) {
            attainmentIds = [moduleAttainment.id];
            gradeAverageResult = this.commonGradeAverageService.calculateGradeAverage(attainmentIds, allAttainments, gradeScale, method);
        } else {
            attainmentIds = this.commonGradeAverageService.getAttainmentIdsForModule(module, validatablePlan);
            gradeAverageResult = this.commonGradeAverageService.calculateGradeAverage(attainmentIds, allAttainments, gradeScale, method);
        }
        const gradeAverage = _.get(gradeAverageResult, 'gradeAverage');
        return _.isNumber(gradeAverage) ? +gradeAverage.toFixed(2) : undefined;
    }

    private getSelectionValidationState(
        validationResult: PlanItemValidationResult,
        parent: EntityWithRule,
        planValidationResult: PlanValidationResult,
    ): SelectionValidationState {
        return {
            invalidSelection: validationResult.invalidSelection,
            hasModuleContentApproval: validationResult.invalidAccordingToModuleContentApproval !== undefined,
            invalidSelectionAccordingToModuleContentApproval: validationResult.invalidAccordingToModuleContentApproval,
            isInvalidAccordingToModuleContentApprovalRequested:
                validationResult.invalidAccordingToModuleContentApproval && !this.isModuleContentApproved(parent, planValidationResult),
            isInvalidAccordingToModuleContentApprovalAccepted:
                validationResult.invalidAccordingToModuleContentApproval && this.isModuleContentApproved(parent, planValidationResult),
        };
    }

    private isModuleContentApproved(module: EntityWithRule, planValidationResult: PlanValidationResult) {
        const moduleValidationResult = _.get(planValidationResult.moduleValidationResults, module.id);
        return moduleValidationResult.isModuleContentApproved;
    }
}
